import { useSidebarState } from '@/hooks/useSidebarState';
import { VacationFilterSidebar } from '../../../components/VacationFilterSidebar';
import { SIDEBAR_STORAGE_KEY } from '../../../components/VacationFilterSidebar/vacationFilterSidebar.definitions';
import { VacationDpTable } from '../../../components/VacationDpTable';
import { MonthYearSelect } from '../../../components/MonthYearSelect';
import type {
    VacationDpCounts,
    VacationDpFilterKey,
    VacationDpRow,
} from '../../../models/vacationDpBoard.types';
import type { DpEmptyReason } from '../../../hooks/useVacationDpBoard';
import {
    ACCESS_DENIED_MESSAGE,
    ACCESS_DENIED_TITLE,
    EMPTY_MESSAGES,
    LOAD_ERROR_MESSAGE,
    LOAD_ERROR_TITLE,
    MONTH_FILTER_LABEL,
    buildResultLabel,
} from '../vacationManagementPage.definitions';
import { DpBoardMessage } from './DpBoardMessage';
import { VacationDpToolbar } from './VacationDpToolbar';

export interface VacationDpBoardProps {
    rows: VacationDpRow[];
    counts: VacationDpCounts;
    totalCount: number;
    visibleCount: number;
    activeFilter: VacationDpFilterKey | null;
    onToggleFilter: (key: VacationDpFilterKey) => void;
    search: string;
    onSearchChange: (value: string) => void;
    selectedMonth: number | null;
    onMonthChange: (value: number | null) => void;
    selectedYear: number | null;
    onYearChange: (value: number | null) => void;
    availableYears?: number[];
    emptyReason: DpEmptyReason;
    onOpenRequest: (row: VacationDpRow) => void;
    onOpenHistory?: (collaborator: { id: string; name: string; email: string }) => void;
    isLoading?: boolean;
    isAccessDenied?: boolean;
    hasError?: boolean;
    today?: Date;
    selectedClient?: string | null;
    onClientChange?: (value: string | null) => void;
    clientOptions?: Array<{ value: string; label: string; count: number }>;
}

/**
 * A visão do painel do DP, sem nenhuma consulta.
 *
 * Recebe tudo por prop de propósito: é o que permite renderizar a tela em teste e em preview sem
 * Firestore, autenticação ou claim de papel. Quem busca os dados é `VacationManagementPage`.
 */
export function VacationDpBoard({
    rows,
    counts,
    totalCount,
    visibleCount,
    activeFilter,
    onToggleFilter,
    search,
    onSearchChange,
    selectedMonth,
    onMonthChange,
    selectedYear,
    onYearChange,
    availableYears,
    emptyReason,
    onOpenRequest,
    onOpenHistory,
    isLoading = false,
    isAccessDenied = false,
    hasError = false,
    today,
    selectedClient,
    onClientChange,
    clientOptions = [],
}: VacationDpBoardProps) {
    const { isCollapsed, toggleSidebar } = useSidebarState(SIDEBAR_STORAGE_KEY, false);

    // Falta de claim vem antes de erro genérico: é a causa mais provável e a única com ação clara.
    if (isAccessDenied) {
        return (
            <DpBoardMessage
                tone="warning"
                title={ACCESS_DENIED_TITLE}
                description={ACCESS_DENIED_MESSAGE}
            />
        );
    }

    if (hasError) {
        return (
            <DpBoardMessage
                tone="danger"
                title={LOAD_ERROR_TITLE}
                description={LOAD_ERROR_MESSAGE}
            />
        );
    }

    // Os selects de período só fazem sentido depois que o DP já restringiu a lista aos cartões de data.
    const showPeriodFilter = activeFilter === 'onVacation' || activeFilter === 'scheduled' || activeFilter === 'vacationsCompleted';

    return (
        <div className="space-y-3">
            <VacationDpToolbar
                search={search}
                onSearchChange={onSearchChange}
                selectedClient={selectedClient}
                onClientChange={onClientChange}
                clientOptions={clientOptions}
                isSidebarCollapsed={isCollapsed}
                onToggleSidebar={toggleSidebar}
            />

            <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
                <VacationFilterSidebar
                    counts={counts}
                    activeFilter={activeFilter}
                    onToggleFilter={onToggleFilter}
                    isLoading={isLoading}
                    isCollapsed={isCollapsed}
                    className="lg:order-last"
                    tourId="dp-status-filters"
                />

                <div className="min-w-0 flex-1 space-y-3">
                    {/* Período e Contagem - segunda linha */}
                    {(showPeriodFilter || !isLoading) && (
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            {showPeriodFilter && (
                                <div data-tour="dp-month">
                                    <MonthYearSelect
                                        month={selectedMonth}
                                        year={selectedYear}
                                        onMonthChange={onMonthChange}
                                        onYearChange={onYearChange}
                                        allowAllMonths
                                        allowAllYears
                                        monthLabel={MONTH_FILTER_LABEL}
                                        yearLabel="Ano"
                                        yearOptions={availableYears}
                                    />
                                </div>
                            )}

                            {!isLoading && (
                                <p className="text-sm text-raro-gray-500 font-montserrat sm:ml-auto">
                                    {buildResultLabel(visibleCount, totalCount)}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Tabela */}
                    <VacationDpTable
                        rows={rows}
                        onOpenRequest={onOpenRequest}
                        onOpenHistory={onOpenHistory}
                        isLoading={isLoading}
                        emptyMessage={EMPTY_MESSAGES[emptyReason]}
                        today={today}
                        activeFilter={activeFilter}
                    />
                </div>
            </div>
        </div>
    );
}

export default VacationDpBoard;
